/**
 * Pure T1–T4 execution-preview model for protocol definition blueprints.
 */

import type {
  ProtocolAtom,
  ProtocolConnection,
  ProtocolCriterion,
  ProtocolGate,
} from "../../api/clinicalTrialsApiTypes";
import { PROTOCOL_TIERS } from "./ProtocolTierFilter";

export type ProtocolFlowTier = (typeof PROTOCOL_TIERS)[number];

export type ProtocolFlowCriterionGroup = {
  criterionId: string;
  criterionNo: string;
  criterionType: string;
  evalOrder: number;
  atomCount: number;
  hardStopCount: number;
  needsReviewCount: number;
  gateLabels: string[];
  atomIds: string[];
  spansMultipleTiers: boolean;
};

export type ProtocolFlowCrossTierEdge = {
  connectionId: string;
  connectionType: string;
  fromAtomId: string;
  toAtomId: string;
  fromTier: string;
  toTier: string;
};

export type ProtocolFlowStage = {
  tier: ProtocolFlowTier;
  label: string;
  description: string;
  atomCount: number;
  criterionCount: number;
  hardStopCount: number;
  needsReviewCount: number;
  gateCount: number;
  conditionalCount: number;
  criterionGroups: ProtocolFlowCriterionGroup[];
};

export type ProtocolFlowModel = {
  stages: ProtocolFlowStage[];
  totalAtomCount: number;
  multiTierCriterionCount: number;
  crossTierEdges: ProtocolFlowCrossTierEdge[];
};

const TIER_META: Record<
  ProtocolFlowTier,
  { label: string; description: string }
> = {
  T1: {
    label: "T1 · Structured checks",
    description:
      "Deterministic structured-data gates that can run in parallel.",
  },
  T2: {
    label: "T2 · Document evidence",
    description: "Pathology, imaging, and report-backed evidence checks.",
  },
  T3: {
    label: "T3 · CRC confirmation",
    description: "CRC attestation and operational confirmation tasks.",
  },
  T4: {
    label: "T4 · PI judgment",
    description: "Investigator judgment and clinical decision points.",
  },
};

/**
 * Build a definition-only T1–T4 execution preview from draft entities.
 *
 * @param criteria - Draft criteria
 * @param atoms - Draft atoms
 * @param gates - Draft gates
 * @param connections - Draft connections
 * @returns Ordered flow stages with metrics and explicit cross-tier edges
 */
export function buildProtocolFlowModel(
  criteria: ProtocolCriterion[],
  atoms: ProtocolAtom[],
  gates: ProtocolGate[],
  connections: ProtocolConnection[],
): ProtocolFlowModel {
  const atomById = new Map(atoms.map((atom) => [atom.atom_id, atom]));
  const criterionById = new Map(
    criteria.map((criterion) => [criterion.criterion_id, criterion]),
  );
  const tiersByCriterion = new Map<string, Set<string>>();
  for (const atom of atoms) {
    const current =
      tiersByCriterion.get(atom.criterion_id) ?? new Set<string>();
    current.add(atom.tier);
    tiersByCriterion.set(atom.criterion_id, current);
  }
  const multiTierCriterionIds = new Set(
    [...tiersByCriterion.entries()]
      .filter(([, tiers]) => tiers.size > 1)
      .map(([criterionId]) => criterionId),
  );

  const stages = PROTOCOL_TIERS.map((tier) => {
    const tierAtoms = atoms.filter((atom) => atom.tier === tier);
    const groups = buildCriterionGroups(
      tierAtoms,
      criterionById,
      gates,
      multiTierCriterionIds,
    );
    const gateLabels = new Set(groups.flatMap((group) => group.gateLabels));
    return {
      tier,
      label: TIER_META[tier].label,
      description: TIER_META[tier].description,
      atomCount: tierAtoms.length,
      criterionCount: groups.length,
      hardStopCount: tierAtoms.filter((atom) => atom.is_hard_stop).length,
      needsReviewCount: tierAtoms.filter((atom) => atom.needs_review).length,
      gateCount: gateLabels.size,
      conditionalCount: tierAtoms.filter((atom) => Boolean(atom.applies_if))
        .length,
      criterionGroups: groups,
    };
  });

  const crossTierEdges = connections
    .filter((connection) =>
      ["DEPENDS_ON", "APPLIES_IF"].includes(connection.connection_type),
    )
    .map((connection) => {
      const fromAtom = atomById.get(connection.from_node_id);
      const toAtom = atomById.get(connection.to_node_id);
      if (!fromAtom || !toAtom || fromAtom.tier === toAtom.tier) return null;
      return {
        connectionId: connection.connection_id,
        connectionType: connection.connection_type,
        fromAtomId: fromAtom.atom_id,
        toAtomId: toAtom.atom_id,
        fromTier: fromAtom.tier,
        toTier: toAtom.tier,
      };
    })
    .filter((edge): edge is ProtocolFlowCrossTierEdge => edge !== null);

  return {
    stages,
    totalAtomCount: atoms.length,
    multiTierCriterionCount: multiTierCriterionIds.size,
    crossTierEdges,
  };
}

/**
 * Group tier atoms by owning criterion for parallel stage rendering.
 *
 * @param tierAtoms - Atoms belonging to one tier
 * @param criterionById - Criterion lookup
 * @param gates - Draft gates
 * @param multiTierCriterionIds - Criteria that span more than one tier
 * @returns Criterion groups ordered by evaluation sequence
 */
function buildCriterionGroups(
  tierAtoms: ProtocolAtom[],
  criterionById: Map<string, ProtocolCriterion>,
  gates: ProtocolGate[],
  multiTierCriterionIds: Set<string>,
): ProtocolFlowCriterionGroup[] {
  const atomsByCriterion = new Map<string, ProtocolAtom[]>();
  for (const atom of tierAtoms) {
    const current = atomsByCriterion.get(atom.criterion_id) ?? [];
    current.push(atom);
    atomsByCriterion.set(atom.criterion_id, current);
  }

  return [...atomsByCriterion.entries()]
    .map(([criterionId, childAtoms]) => {
      const criterion = criterionById.get(criterionId);
      const gateLabels = [
        ...new Set(
          childAtoms
            .map((atom) => atom.logic_group)
            .filter((label): label is string => Boolean(label)),
        ),
      ];
      for (const gate of gates.filter(
        (item) => item.criterion_id === criterionId,
      )) {
        if (!gateLabels.includes(gate.label)) gateLabels.push(gate.label);
      }
      return {
        criterionId,
        criterionNo: criterion?.criterion_no ?? criterionId,
        criterionType: criterion?.criterion_type ?? "Unknown",
        evalOrder: criterion?.eval_order ?? Number.MAX_SAFE_INTEGER,
        atomCount: childAtoms.length,
        hardStopCount: childAtoms.filter((atom) => atom.is_hard_stop).length,
        needsReviewCount: childAtoms.filter((atom) => atom.needs_review).length,
        gateLabels,
        atomIds: childAtoms.map((atom) => atom.atom_id),
        spansMultipleTiers: multiTierCriterionIds.has(criterionId),
      };
    })
    .sort((left, right) => left.evalOrder - right.evalOrder);
}
